'use client';

import { Button, Chip, Select, SelectItem, Spinner, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import { CONVERSATIONS_PAGE_SIZE_OPTIONS } from '../../constants';
import type { ReportFolderItemProps } from '../../props';
import { ReportItem } from './report-item';

export function ReportFolderItem({
    folder,
    reports,
    isExpanded,
    isDragOver,
    isFetching,
    allFolders,
    page,
    totalPages,
    totalItems,
    limit,
    onPageChange,
    onLimitChange,
    onToggle,
    onReportSelect,
    onReportDelete,
    onReportRename,
    onReportShare,
    onMoveReportToFolder,
    onRenameFolder,
    onDeleteFolder,
    onDragOver,
    onDragLeave,
    onDrop,
}: ReportFolderItemProps) {
    return (
        <div
            className={`overflow-hidden rounded-xl border transition-colors ${isDragOver ? 'border-primary bg-primary/5' : 'border-default-200'}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {/* Folder header */}
            <div
                className="group flex cursor-pointer items-center gap-3 bg-default-50/50 px-4 py-3 transition-colors hover:bg-default-100/60"
                onClick={onToggle}
            >
                <Icon
                    icon={isExpanded ? 'lucide:chevron-down' : 'lucide:chevron-right'}
                    width={14}
                    className="shrink-0 text-default-400"
                />
                <Icon
                    icon={isExpanded ? 'lucide:folder-open' : 'lucide:folder'}
                    width={16}
                    className="shrink-0 text-warning"
                />
                <span className="flex-1 truncate text-small font-medium text-foreground">
                    {folder.name}
                </span>
                {isDragOver && (
                    <span className="text-mini font-medium text-primary">Drop here</span>
                )}
                <Chip size="sm" variant="flat" className="h-5 min-w-0 px-1.5 text-mini">
                    {totalItems}
                </Chip>
                <div
                    className="flex items-center gap-0.5 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip content="Rename" size="sm">
                        <Button
                            size="sm"
                            variant="light"
                            onPress={onRenameFolder}
                            aria-label="Rename folder"
                            isIconOnly
                        >
                            <Icon icon="lucide:edit-2" width={12} />
                        </Button>
                    </Tooltip>
                    <Tooltip content="Delete" size="sm">
                        <Button
                            size="sm"
                            variant="light"
                            onPress={onDeleteFolder}
                            aria-label="Delete folder"
                            isIconOnly
                        >
                            <Icon icon="lucide:trash-2" width={12} className="text-danger" />
                        </Button>
                    </Tooltip>
                </div>
            </div>

            {/* Expanded report list */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden"
                    >
                        {reports.length === 0 ? (
                            <div className="border-t border-default-100 px-4 py-6 text-center">
                                {isFetching ? (
                                    <Spinner size="sm" color="primary" />
                                ) : (
                                    <p className="text-small text-default-400">
                                        {isDragOver
                                            ? 'Drop to add to this folder'
                                            : 'No reports in this folder'}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="divide-y divide-default-100 border-t border-default-100">
                                    {reports.map((r) => (
                                        <div
                                            key={r.id}
                                            className="pl-2"
                                            draggable
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData(
                                                    'text/plain',
                                                    r.conversationId,
                                                );
                                                e.dataTransfer.effectAllowed = 'move';
                                            }}
                                        >
                                            <ReportItem
                                                report={r}
                                                onSelect={() => onReportSelect(r)}
                                                onRename={() => onReportRename(r.conversationId)}
                                                onDelete={() => onReportDelete(r.conversationId)}
                                                onShare={() => onReportShare(r.conversationId)}
                                                folders={allFolders}
                                                onMoveToFolder={(fid) =>
                                                    onMoveReportToFolder(r.conversationId, fid)
                                                }
                                            />
                                        </div>
                                    ))}
                                </div>
                                {totalItems > 0 && (
                                    <div className="flex items-center justify-between border-t border-divider px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-mini text-default-400">Show</span>
                                            <Select
                                                size="sm"
                                                selectedKeys={[String(limit)]}
                                                onSelectionChange={(keys) => {
                                                    const val = Number(Array.from(keys)[0]);
                                                    if (val) onLimitChange(val);
                                                }}
                                                classNames={{
                                                    base: 'w-20',
                                                    trigger: 'h-7 min-h-7',
                                                }}
                                                aria-label="Items per page"
                                            >
                                                {CONVERSATIONS_PAGE_SIZE_OPTIONS.map((opt) => (
                                                    <SelectItem key={String(opt)}>
                                                        {String(opt)}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                            <span className="text-mini text-default-400">
                                                of {totalItems}
                                            </span>
                                        </div>
                                        {totalPages > 1 && (
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="flat"
                                                    isIconOnly
                                                    isDisabled={page <= 1}
                                                    onPress={() => onPageChange(page - 1)}
                                                    className="h-7 w-7 min-w-7"
                                                    aria-label="Previous page"
                                                >
                                                    <Icon icon="lucide:chevron-left" width={14} />
                                                </Button>
                                                <span className="px-2 text-small text-default-600">
                                                    {page} / {totalPages}
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="flat"
                                                    isIconOnly
                                                    isDisabled={page >= totalPages}
                                                    onPress={() => onPageChange(page + 1)}
                                                    className="h-7 w-7 min-w-7"
                                                    aria-label="Next page"
                                                >
                                                    <Icon icon="lucide:chevron-right" width={14} />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
